import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button,
  TextField, FormControl, InputLabel, Select, MenuItem,
  IconButton, Checkbox, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Divider, Alert, Pagination, Card, CardContent,
  Menu, ButtonGroup
} from '@mui/material';
import {
  Print as PrintIcon,
  LocalShipping as ShippingIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Assessment as AssessmentIcon,
  ListAlt as ListAltIcon,
  Receipt as ReceiptIcon,
  ArrowDropDown as ArrowDropDownIcon,
  LocalShippingOutlined as LogisticsIcon,
  QrCode as QrCodeIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

const statusColors = {
  pending: 'warning',
  paid: 'info',
  shipping: 'primary',
  delivered: 'success',
  cancelled: 'error'
};

const AdminOrders = () => {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();

  const statusLabels = {
    pending: t('orders.status_pending'),
    paid: t('orders.status_paid'),
    shipping: t('orders.status_shipping'),
    delivered: t('orders.status_delivered'),
    cancelled: t('orders.status_cancelled')
  };

  const paymentLabels = {
    linepay: 'LINE Pay',
    smilepay: t('orders.payment_cvs_code')
  };
  const printRef = useRef();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    payment_status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [printDialog, setPrintDialog] = useState(false);
  const [printLabels, setPrintLabels] = useState([]);
  const [printType, setPrintType] = useState('shipping'); // 'shipping', 'picking', 'order'
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [pickingList, setPickingList] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [logisticsLabels, setLogisticsLabels] = useState([]);
  const [creatingLogistics, setCreatingLogistics] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [filters, pagination.page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        ...filters
      });
      const response = await api.get(`/admin/orders?${params}`);
      if (response.data.success) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      toast.error(t('orders.load_failed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('取得統計失敗');
    }
  };

  // 查看訂單詳情
  const handleViewDetail = async (orderNo) => {
    try {
      const response = await api.get(`/admin/orders/${orderNo}`);
      if (response.data.success) {
        setSelectedOrder(response.data.order);
        setDetailDialog(true);
      }
    } catch (error) {
      toast.error(t('orders.detail_load_failed'));
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(orders.map(o => o.order_no));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (orderNo) => {
    setSelected(prev =>
      prev.includes(orderNo)
        ? prev.filter(n => n !== orderNo)
        : [...prev, orderNo]
    );
  };

  const handleBatchStatus = async (newStatus) => {
    if (selected.length === 0) {
      toast.warning(t('orders.select_orders'));
      return;
    }

    try {
      await api.put('/admin/orders/batch/status', {
        orderNos: selected,
        status: newStatus
      });
      toast.success(t('orders.batch_updated', { count: selected.length }));
      setSelected([]);
      fetchOrders();
      fetchStats();
    } catch (error) {
      toast.error(t('common.update_failed'));
    }
  };

  // 列印出貨單
  const handlePrintShipping = async () => {
    if (selected.length === 0) {
      toast.warning(t('orders.select_to_print'));
      return;
    }

    try {
      const response = await api.post('/admin/orders/shipping-labels', {
        orderNos: selected
      });
      if (response.data.success) {
        setPrintLabels(response.data.labels);
        setPrintType('shipping');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error(t('orders.shipping_label_failed'));
    }
  };

  // 列印撿貨單
  const handlePrintPicking = async () => {
    if (selected.length === 0) {
      toast.warning(t('orders.select_to_print'));
      return;
    }

    try {
      const response = await api.post('/admin/orders/picking-list', {
        orderNos: selected
      });
      if (response.data.success) {
        setPickingList(response.data.pickingList);
        setPrintType('picking');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error(t('orders.picking_list_failed'));
    }
  };

  // 列印訂單明細
  const handlePrintOrders = async () => {
    if (selected.length === 0) {
      toast.warning(t('orders.select_to_print'));
      return;
    }

    try {
      const response = await api.post('/admin/orders/order-details', {
        orderNos: selected
      });
      if (response.data.success) {
        setOrderDetails(response.data.orders);
        setPrintType('order');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error(t('orders.order_details_failed'));
    }
  };

  // 舊的 handlePrint 保留相容性
  const handlePrint = handlePrintShipping;

  // 建立物流單
  const handleCreateLogistics = async () => {
    if (selected.length === 0) {
      toast.warning(t('orders.select_paid_orders'));
      return;
    }

    setCreatingLogistics(true);
    try {
      const response = await api.post('/smilepay/logistics/batch-create', {
        order_nos: selected
      });
      if (response.data.success) {
        const { created, failed, results } = response.data;
        if (created > 0) {
          toast.success(t('orders.logistics_created', { count: created }));
        }
        if (failed > 0) {
          const failedOrders = results.filter(r => !r.success);
          failedOrders.forEach(o => {
            toast.warning(`${o.orderNo}: ${o.error || '建立失敗'}`);
          });
        }
        fetchOrders();
      }
    } catch (error) {
      toast.error(t('orders.logistics_create_failed') + ': ' + (error.response?.data?.error || error.message));
    } finally {
      setCreatingLogistics(false);
    }
  };

  // 列印物流標籤
  const handlePrintLogistics = async () => {
    if (selected.length === 0) {
      toast.warning(t('orders.select_to_print'));
      return;
    }

    try {
      const response = await api.post('/smilepay/logistics/labels', {
        order_nos: selected
      });
      if (response.data.success) {
        setLogisticsLabels(response.data.labels);
        setPrintType('logistics');
        setPrintDialog(true);
      }
    } catch (error) {
      toast.error(t('orders.logistics_label_failed'));
    }
  };

  const doPrint = () => {
    const printContent = document.getElementById('print-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>出貨單列印</title>
          <style>
            body { font-family: Arial, sans-serif; }
            .label { page-break-after: always; padding: 20px; border: 1px dashed #ccc; margin-bottom: 20px; }
            .label:last-child { page-break-after: auto; }
            .header { font-size: 18px; font-weight: bold; margin-bottom: 10px; border-bottom: 2px solid #000; padding-bottom: 5px; }
            .store-info { background: #f5f5f5; padding: 10px; margin: 10px 0; }
            .store-name { font-size: 16px; font-weight: bold; }
            .customer { margin: 10px 0; }
            .items { margin: 10px 0; }
            .item { padding: 5px 0; border-bottom: 1px dotted #ccc; }
            .total { font-size: 16px; font-weight: bold; text-align: right; margin-top: 10px; }
            .barcode { text-align: center; font-family: monospace; font-size: 24px; letter-spacing: 3px; margin: 15px 0; }
            .remark { background: #fff3cd; padding: 8px; margin-top: 10px; }
            @media print {
              .label { border: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('zh-TW');
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* 頂部統計 */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('dashboard.today_orders')}</Typography>
              <Typography variant="h4" color="primary">{stats.today_orders || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('orders.pending_shipment')}</Typography>
              <Typography variant="h4" color="warning.main">{stats.paid_orders || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('orders.status_shipping')}</Typography>
              <Typography variant="h4" color="info.main">{stats.shipping_orders || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body2">{t('dashboard.total_revenue')}</Typography>
              <Typography variant="h4" color="success.main">
                ${(stats.total_revenue || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 標題與操作 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">{t('orders.title')}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            color="info"
            startIcon={<AssessmentIcon />}
            onClick={() => navigate('/admin/analytics')}
          >
            {t('analytics.title')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => { fetchOrders(); fetchStats(); }}
          >
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* 篩選 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label={t('orders.search_placeholder')}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              inputProps={{ 'data-testid': 'admin-orders-search' }}
              InputProps={{
                endAdornment: <SearchIcon color="action" />
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('orders.order_status')}</InputLabel>
              <Select
                value={filters.status}
                label={t('orders.order_status')}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="pending">{t('orders.status_pending')}</MenuItem>
                <MenuItem value="paid">{t('orders.status_paid')}</MenuItem>
                <MenuItem value="shipping">{t('orders.status_shipping')}</MenuItem>
                <MenuItem value="delivered">{t('orders.status_delivered')}</MenuItem>
                <MenuItem value="cancelled">{t('orders.status_cancelled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('orders.payment_status')}</InputLabel>
              <Select
                value={filters.payment_status}
                label={t('orders.payment_status')}
                onChange={(e) => setFilters(prev => ({ ...prev, payment_status: e.target.value }))}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="unpaid">{t('orders.unpaid')}</MenuItem>
                <MenuItem value="paid">{t('orders.status_paid')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* 列印下拉選單 */}
              <ButtonGroup variant="contained" disabled={selected.length === 0}>
                <Button
                  startIcon={<PrintIcon />}
                  onClick={handlePrintShipping}
                >
                  {t('orders.print')} ({selected.length})
                </Button>
                <Button
                  size="small"
                  onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={printMenuAnchor}
                open={Boolean(printMenuAnchor)}
                onClose={() => setPrintMenuAnchor(null)}
              >
                <MenuItem onClick={() => { handlePrintShipping(); setPrintMenuAnchor(null); }}>
                  <ShippingIcon sx={{ mr: 1 }} /> {t('orders.shipping_label')}
                </MenuItem>
                <MenuItem onClick={() => { handlePrintPicking(); setPrintMenuAnchor(null); }}>
                  <ListAltIcon sx={{ mr: 1 }} /> {t('orders.picking_list')}
                </MenuItem>
                <MenuItem onClick={() => { handlePrintOrders(); setPrintMenuAnchor(null); }}>
                  <ReceiptIcon sx={{ mr: 1 }} /> {t('orders.order_details')}
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { handlePrintLogistics(); setPrintMenuAnchor(null); }}>
                  <QrCodeIcon sx={{ mr: 1 }} /> {t('orders.logistics_label')}
                </MenuItem>
              </Menu>
              <Button
                variant="contained"
                color="warning"
                startIcon={<LogisticsIcon />}
                onClick={handleCreateLogistics}
                disabled={selected.length === 0 || creatingLogistics}
              >
                {creatingLogistics ? t('orders.creating') : t('orders.create_logistics')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ShippingIcon />}
                onClick={() => handleBatchStatus('shipping')}
                disabled={selected.length === 0}
              >
                {t('orders.mark_shipped')}
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => handleBatchStatus('delivered')}
                disabled={selected.length === 0}
              >
                {t('orders.mark_delivered')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* 訂單列表 */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selected.length === orders.length && orders.length > 0}
                  indeterminate={selected.length > 0 && selected.length < orders.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('orders.order_no')}</TableCell>
              <TableCell>{t('orders.recipient')}</TableCell>
              <TableCell>{t('orders.pickup_store')}</TableCell>
              <TableCell>{t('orders.products')}</TableCell>
              <TableCell align="right">{t('orders.amount')}</TableCell>
              <TableCell>{t('orders.payment_method')}</TableCell>
              <TableCell>{t('common.status')}</TableCell>
              <TableCell>{t('orders.created_at')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map(order => (
              <TableRow
                key={order.order_no}
                data-testid={`order-row-${order.order_no}`}
                hover
                selected={selected.includes(order.order_no)}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(order.order_no)}
                    onChange={() => handleSelect(order.order_no)}
                  />
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="primary"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => handleViewDetail(order.order_no)}
                  >
                    {order.order_no}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.customer_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.customer_phone}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{order.store_type}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.store_name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 200 }}>
                  <Typography variant="caption" noWrap title={order.products}>
                    {order.products?.substring(0, 40)}...
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight="bold" color="error">
                    ${order.total_amount}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={paymentLabels[order.payment_method] || order.payment_method}
                    color={order.payment_method === 'linepay' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusLabels[order.status]}
                    color={statusColors[order.status]}
                    data-testid={`order-status-${order.order_no}`}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {formatDate(order.created_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {loading ? t('common.loading') : t('orders.no_data')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 分頁 */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Pagination
          count={pagination.pages}
          page={pagination.page}
          onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
          color="primary"
        />
      </Box>

      {/* 列印對話框 */}
      <Dialog open={printDialog} onClose={() => setPrintDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {printType === 'shipping' && t('orders.shipping_preview', { count: printLabels.length })}
          {printType === 'picking' && t('orders.picking_preview', { count: pickingList.length })}
          {printType === 'order' && t('orders.order_detail_preview', { count: orderDetails.length })}
          {printType === 'logistics' && t('orders.logistics_preview', { count: logisticsLabels.length })}
        </DialogTitle>
        <DialogContent>
          <div id="print-content">
            {/* 出貨單列印 */}
            {printType === 'shipping' && printLabels.map((label, index) => (
              <div key={label.orderNo} className="label" style={{
                border: '1px dashed #ccc',
                padding: 20,
                marginBottom: 20,
                pageBreakAfter: index < printLabels.length - 1 ? 'always' : 'auto'
              }}>
                <div className="header" style={{ borderBottom: '2px solid #000', paddingBottom: 8, marginBottom: 15 }}>
                  <Typography variant="h6">清倉特賣 - 出貨單</Typography>
                  <Typography variant="body2" color="text.secondary">
                    訂單編號: {label.orderNo}
                  </Typography>
                </div>

                {label.storeName && (
                <div className="store-info" style={{ background: '#f5f5f5', padding: 12, marginBottom: 15 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {label.storeType} - {label.storeName}
                  </Typography>
                  <Typography variant="body2">{label.storeAddress}</Typography>
                  {label.storeId && <Typography variant="caption">門市代號: {label.storeId}</Typography>}
                </div>
                )}

                <div className="customer" style={{ marginBottom: 15 }}>
                  <Typography variant="subtitle2">收件人資訊</Typography>
                  <Typography>姓名: {label.customerName}</Typography>
                  <Typography>電話: {label.customerPhone}</Typography>
                </div>

                <Divider sx={{ my: 2 }} />

                <div className="items">
                  <Typography variant="subtitle2" gutterBottom>商品明細</Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                    {label.items}
                  </Typography>
                </div>

                <div className="total" style={{ textAlign: 'right', marginTop: 15 }}>
                  <Typography variant="h6">總計: ${label.totalAmount}</Typography>
                </div>

                {label.remark && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    備註: {label.remark}
                  </Alert>
                )}

                <div className="barcode" style={{
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  fontSize: 20,
                  letterSpacing: 3,
                  marginTop: 20,
                  padding: 10,
                  background: '#f0f0f0'
                }}>
                  {label.orderNo}
                </div>
              </div>
            ))}

            {/* 撿貨單列印 */}
            {printType === 'picking' && (
              <div style={{ padding: 20 }}>
                <div style={{ borderBottom: '2px solid #000', paddingBottom: 10, marginBottom: 20 }}>
                  <Typography variant="h5" fontWeight="bold">撿貨單</Typography>
                  <Typography variant="body2" color="text.secondary">
                    列印時間: {new Date().toLocaleString('zh-TW')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    訂單數量: {selected.length} 筆
                  </Typography>
                </div>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell width={60}>序號</TableCell>
                      <TableCell>商品名稱</TableCell>
                      <TableCell width={100}>SKU</TableCell>
                      <TableCell width={80} align="center">總數量</TableCell>
                      <TableCell>訂單編號</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pickingList.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="center">
                          <Typography fontWeight="bold" color="primary">
                            {item.totalQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                            {item.orderNos}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                  <Typography variant="body2">
                    總計: {pickingList.reduce((sum, item) => sum + item.totalQuantity, 0)} 件商品
                  </Typography>
                </Box>
              </div>
            )}

            {/* 訂單明細列印 */}
            {printType === 'order' && orderDetails.map((order, index) => (
              <div key={order.orderNo} style={{
                border: '1px solid #ccc',
                padding: 20,
                marginBottom: 20,
                pageBreakAfter: index < orderDetails.length - 1 ? 'always' : 'auto'
              }}>
                <div style={{ borderBottom: '2px solid #000', paddingBottom: 10, marginBottom: 15 }}>
                  <Typography variant="h6" fontWeight="bold">訂單明細</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">訂單編號: {order.orderNo}</Typography>
                      <Typography variant="body2">訂單日期: {new Date(order.createdAt).toLocaleString('zh-TW')}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">付款方式: {order.paymentMethod}</Typography>
                      <Typography variant="body2">付款狀態: {order.paymentStatus}</Typography>
                    </Grid>
                  </Grid>
                </div>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">收件人資訊</Typography>
                    <Typography variant="body2">姓名: {order.customerName}</Typography>
                    <Typography variant="body2">電話: {order.customerPhone}</Typography>
                    <Typography variant="body2">Email: {order.customerEmail || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    {order.storeName ? (
                      <>
                        <Typography variant="subtitle2" fontWeight="bold">取貨門市</Typography>
                        <Typography variant="body2">{order.storeType} - {order.storeName}</Typography>
                        <Typography variant="body2">{order.storeAddress}</Typography>
                        {order.storeId && <Typography variant="body2">門市代號: {order.storeId}</Typography>}
                      </>
                    ) : (
                      <>
                        <Typography variant="subtitle2" fontWeight="bold">配送方式</Typography>
                        <Typography variant="body2">{order.shippingAddress || '宅配'}</Typography>
                      </>
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>商品明細</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>商品名稱</TableCell>
                      <TableCell width={100}>SKU</TableCell>
                      <TableCell width={80} align="center">數量</TableCell>
                      <TableCell width={100} align="right">單價</TableCell>
                      <TableCell width={100} align="right">小計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.sku}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${item.price}</TableCell>
                        <TableCell align="right">${item.price * item.quantity}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="body2">商品小計: ${order.subtotal}</Typography>
                  <Typography variant="body2">運費: ${order.shippingFee || 0}</Typography>
                  <Typography variant="h6" fontWeight="bold" color="error">
                    總計: ${order.totalAmount}
                  </Typography>
                </Box>

                {order.remark && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    訂單備註: {order.remark}
                  </Alert>
                )}
              </div>
            ))}

            {/* 物流標籤列印 */}
            {printType === 'logistics' && logisticsLabels.map((label, index) => (
              <div key={label.orderNo} className="label" style={{
                border: '2px solid #000',
                padding: 20,
                marginBottom: 20,
                pageBreakAfter: index < logisticsLabels.length - 1 ? 'always' : 'auto'
              }}>
                <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: 10, marginBottom: 15 }}>
                  <Typography variant="h5" fontWeight="bold">{label.storeTypeName}</Typography>
                  <Typography variant="body2">超商取貨</Typography>
                </div>

                {/* 物流單號區 */}
                <Box sx={{
                  background: '#f0f0f0',
                  p: 2,
                  textAlign: 'center',
                  mb: 2,
                  border: '1px solid #ccc'
                }}>
                  {label.smilePayNo ? (
                    <>
                      <Typography variant="caption" display="block">物流單號</Typography>
                      <Typography variant="h4" fontFamily="monospace" fontWeight="bold">
                        {label.smilePayNo}
                      </Typography>
                      {label.trackingNo && (
                        <Typography variant="body2" color="text.secondary">
                          追蹤號: {label.trackingNo}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Alert severity="warning" sx={{ mb: 0 }}>
                      尚未建立物流單
                    </Alert>
                  )}
                </Box>

                {/* 門市資訊 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">取貨門市</Typography>
                  <Typography variant="h6">{label.storeName}</Typography>
                  <Typography variant="body2">{label.storeAddress}</Typography>
                  <Typography variant="caption">門市代號: {label.storeId}</Typography>
                </Box>

                {/* 收件人資訊 */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">收件人</Typography>
                  <Typography variant="body1">{label.customerName}</Typography>
                  <Typography variant="body2">{label.customerPhone}</Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* 訂單資訊 */}
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption">訂單編號</Typography>
                    <Typography variant="body2" fontWeight="bold">{label.orderNo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">訂單金額</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error">
                      ${label.totalAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">物流狀態</Typography>
                    <Typography variant="body2">{label.logisticsStatusText}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">列印時間</Typography>
                    <Typography variant="body2">
                      {new Date().toLocaleString('zh-TW')}
                    </Typography>
                  </Grid>
                </Grid>

                {/* 商品清單 */}
                <Box sx={{ mt: 2, p: 1, bgcolor: '#f9f9f9' }}>
                  <Typography variant="caption" fontWeight="bold">商品內容</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {label.items}
                  </Typography>
                </Box>

                {/* 條碼區 (如果有物流單號) */}
                {label.smilePayNo && (
                  <Box sx={{
                    textAlign: 'center',
                    mt: 2,
                    p: 1,
                    border: '1px solid #000',
                    fontFamily: 'monospace',
                    fontSize: 24,
                    letterSpacing: 3
                  }}>
                    {label.smilePayNo}
                  </Box>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialog(false)}>{t('common.close')}</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={doPrint}>
            {t('orders.print')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 訂單詳情對話框 */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('orders.order_detail')} - {selectedOrder?.order_no}
        </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Grid container spacing={3}>
              {/* 訂單基本資訊 */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Chip
                    label={statusLabels[selectedOrder.status] || selectedOrder.status}
                    color={statusColors[selectedOrder.status] || 'default'}
                  />
                  <Chip
                    label={selectedOrder.payment_status === 'paid' ? t('orders.status_paid') : t('orders.unpaid')}
                    color={selectedOrder.payment_status === 'paid' ? 'success' : 'warning'}
                    variant="outlined"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {t('orders.created_at')}: {new Date(selectedOrder.created_at).toLocaleString('zh-TW')}
                  </Typography>
                </Box>
              </Grid>

              {/* 收件人資訊 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      📦 收件人資訊
                    </Typography>
                    <Typography variant="body2">姓名: {selectedOrder.customer_name}</Typography>
                    <Typography variant="body2">電話: {selectedOrder.customer_phone}</Typography>
                    <Typography variant="body2">Email: {selectedOrder.customer_email || '-'}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* 付款資訊 */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      💳 付款資訊
                    </Typography>
                    <Typography variant="body2">
                      付款方式: {paymentLabels[selectedOrder.payment_method] || selectedOrder.payment_method || '超商取貨付款'}
                    </Typography>
                    <Typography variant="body2">
                      付款狀態: {selectedOrder.payment_status === 'paid' ? '已付款' : '未付款'}
                    </Typography>
                    {selectedOrder.paid_at && (
                      <Typography variant="body2">
                        付款時間: {new Date(selectedOrder.paid_at).toLocaleString('zh-TW')}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* 取貨門市/配送資訊 */}
              {selectedOrder.store_name ? (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: '#f5f5f5' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        🏪 取貨門市
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedOrder.store_type === '7-ELEVEN' ? '7-ELEVEN' : selectedOrder.store_type} - {selectedOrder.store_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        地址: {selectedOrder.store_address}
                      </Typography>
                      {selectedOrder.store_id && (
                        <Typography variant="body2" color="text.secondary">
                          門市代號: {selectedOrder.store_id}
                        </Typography>
                      )}
                      {selectedOrder.smilepay_no && (
                        <Alert severity="info" sx={{ mt: 1 }}>
                          物流單號: {selectedOrder.smilepay_no}
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ) : selectedOrder.shipping_address ? (
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ bgcolor: '#f5f5f5' }}>
                    <CardContent>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        🚚 配送地址
                      </Typography>
                      <Typography variant="body2">
                        {selectedOrder.shipping_address}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ) : null}

              {/* 商品明細 */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  🛒 商品明細
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell>商品名稱</TableCell>
                      <TableCell width={100}>SKU</TableCell>
                      <TableCell width={80} align="center">數量</TableCell>
                      <TableCell width={100} align="right">單價</TableCell>
                      <TableCell width={100} align="right">小計</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>{item.product_sku}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">${item.price}</TableCell>
                        <TableCell align="right">${item.subtotal}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              {/* 金額摘要 */}
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'right', mt: 2 }}>
                  <Typography variant="body2">商品小計: ${selectedOrder.subtotal}</Typography>
                  <Typography variant="body2">運費: ${selectedOrder.shipping_fee || 60}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" fontWeight="bold" color="error">
                    總計: ${selectedOrder.total_amount}
                  </Typography>
                </Box>
              </Grid>

              {/* 訂單備註 */}
              {selectedOrder.remark && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    訂單備註: {selectedOrder.remark}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminOrders;
